import { UseFormReturn } from 'react-hook-form';
import { HeartPulse } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { BasePerson } from '@/dtos/Persons/index';

interface HealthProfileFormValues {
  isSmoker: boolean;
  isFormerSmoker: boolean;
  hasDiabetes: boolean;
  hasHypertension: boolean;
  hasDyslipidemia: boolean;
  bodyMassIndex?: number;
  systolicBloodPressure?: number;
  familyHistoryCardiovascularDisease: boolean;
}

interface HealthProfileFormProps {
  form: UseFormReturn<HealthProfileFormValues>;
  selectedPerson: BasePerson | null;
}

export default function HealthProfileForm({ form, selectedPerson }: HealthProfileFormProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <HeartPulse className="h-5 w-5" />
          Profil de Sănătate
        </CardTitle>
        <CardDescription>
          {selectedPerson?.healthProfile
            ? 'Modificați profilul de sănătate existent.'
            : 'Completați profilul de sănătate pentru pacient.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="bmi">IMC (Body Mass Index)</Label>
            <Input
              id="bmi"
              type="number"
              step="0.1"
              {...form.register('bodyMassIndex')}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="sbp">Tensiune Sistolică (mmHg)</Label>
            <Input
              id="sbp"
              type="number"
              {...form.register('systolicBloodPressure')}
            />
          </div>
        </div>

        <div className="space-y-3">
          <Label>Condiții Medicale</Label>
          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="smoker"
                checked={form.watch('isSmoker')}
                onCheckedChange={(checked) => form.setValue('isSmoker', !!checked)}
              />
              <Label htmlFor="smoker" className="font-normal cursor-pointer">
                Fumător
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="formerSmoker"
                checked={form.watch('isFormerSmoker')}
                onCheckedChange={(checked) => form.setValue('isFormerSmoker', !!checked)}
              />
              <Label htmlFor="formerSmoker" className="font-normal cursor-pointer">
                Fost Fumător
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="diabetes"
                checked={form.watch('hasDiabetes')}
                onCheckedChange={(checked) => form.setValue('hasDiabetes', !!checked)}
              />
              <Label htmlFor="diabetes" className="font-normal cursor-pointer">
                Diabet
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="hypertension"
                checked={form.watch('hasHypertension')}
                onCheckedChange={(checked) => form.setValue('hasHypertension', !!checked)}
              />
              <Label htmlFor="hypertension" className="font-normal cursor-pointer">
                Hipertensiune
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="dyslipidemia"
                checked={form.watch('hasDyslipidemia')}
                onCheckedChange={(checked) => form.setValue('hasDyslipidemia', !!checked)}
              />
              <Label htmlFor="dyslipidemia" className="font-normal cursor-pointer">
                Dislipidemie
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="cvHistory"
                checked={form.watch('familyHistoryCardiovascularDisease')}
                onCheckedChange={(checked) =>
                  form.setValue('familyHistoryCardiovascularDisease', !!checked)
                }
              />
              <Label htmlFor="cvHistory" className="font-normal cursor-pointer">
                Istoric Cardiovascular Familial
              </Label>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
